/**
 * manager-product.js
 * Gerencia quick-add ao carrinho nos cards de produto.
 * Disponível como window.ProductManager.
 *
 * Dependências (devem carregar antes):
 *   - window.CartManager    (manager-cart.js)
 *   - window.ButtonFeedback (manager-global-button-feedback.js)
 *
 * ─── HTML esperado (.productCard) ─────────────────────────────────────────
 *
 *   <article class="productCard">
 *
 *     <!-- Botão que abre/fecha o popup de tamanhos -->
 *     <button data-product-add-toggle aria-expanded="false">
 *       Adicionar
 *     </button>
 *
 *     <!-- Popup de tamanhos -->
 *     <div data-size-popup hidden>
 *       <button class="productCard__sizeOption"
 *               data-size-option
 *               data-variant-id="12345678">P</button>
 *       <button class="productCard__sizeOption"
 *               data-size-option
 *               data-variant-id="12345679">M</button>
 *     </div>
 *
 *   </article>
 *
 * ─── Data attributes ──────────────────────────────────────────────────────
 *
 *   .productCard               → container do card (escopo do popup)
 *   data-product-add-toggle    → abre/fecha popup
 *   data-size-popup            → container do popup
 *   data-size-option           → opção de tamanho (lê data-variant-id)
 *   data-variant-id            → ID da variante a adicionar
 *
 * ─── Comportamento ────────────────────────────────────────────────────────
 *
 *   - Click em data-product-add-toggle → abre popup do card / fecha outros
 *   - Click em data-size-option → CartManager.add() + fecha popup
 *   - Click fora de qualquer popup → fecha todos
 *   - Funciona com cards dinâmicos (event delegation em document)
 */

class ProductManager {
  constructor() {
    this._onClick = this._onClick.bind(this);
    this._onKeydown = this._onKeydown.bind(this);
    this._onResize = this._onResize.bind(this);
    this._skuStates = new WeakMap();
    this._openSkuCards = new Set();
    document.addEventListener('click', this._onClick);
    document.addEventListener('keydown', this._onKeydown);
    window.addEventListener('resize', this._onResize);
  }

  // ─── Event delegation ─────────────────────────────────────────────────────

  _onClick(e) {
    const skuButton = e.target.closest('[data-open-sku-modal]');
    if (skuButton) {
      e.preventDefault();
      e.stopPropagation();

      if (!window.matchMedia('(min-width: 768px)').matches) {
        const card = skuButton.closest('.productCard');
        if (card) this._toggleSkuModal(card);
      }
      return;
    }

    const customSelectTrigger = e.target.closest('[data-custom-select-trigger]');
    if (customSelectTrigger) {
      e.preventDefault();
      e.stopPropagation();
      const optionsScope = customSelectTrigger.closest('[data-product-options]');
      this._updateVariantAvailability(this._findCardForOptions(optionsScope), optionsScope);
      this._toggleCustomSelect(customSelectTrigger.closest('[data-custom-select]'));
      return;
    }

    const customSelectOption = e.target.closest('[data-custom-select-option]');
    if (customSelectOption) {
      e.preventDefault();
      e.stopPropagation();
      if (customSelectOption.disabled || customSelectOption.getAttribute('aria-disabled') === 'true') return;
      this._selectCustomOption(customSelectOption.closest('[data-custom-select]'), customSelectOption);
      return;
    }

    // Toggle popup
    const toggleBtn = e.target.closest('[data-product-add-toggle]');
    if (toggleBtn) {
      e.stopPropagation();
      const card   = toggleBtn.closest('.productCard');
      if (!card) return;
      const isOpen = card.querySelector('[data-size-popup]')?.classList.contains('is-open');
      this._closeAllPopups();
      if (!isOpen) this._openPopup(card);
      return;
    }

    // Selecionar tamanho → add to cart
    const selectedOption = e.target.closest('[data-product-add-selected]');
    if (selectedOption) {
      const optionsScope = selectedOption.closest('[data-product-options]');
      const card = selectedOption.closest('.productCard') || this._findCardForOptions(optionsScope);
      const selection = card ? this._getSelectedVariant(card, optionsScope) : null;
      if (selection?.optionSelects.length > 0 && !selection.complete) {
        const productUrl = card?.dataset.productUrl || selectedOption.dataset.productUrl;
        if (productUrl) window.location.assign(productUrl);
        return;
      }

      const variantId = this._resolveSelectedVariant(selectedOption);
      if (variantId) this._addToCart(selectedOption, variantId);
      return;
    }

    // Selecionar cor (swatch) no card
    const colorSwatch = e.target.closest('[data-color-swatch]');
    if (colorSwatch) {
      const card = colorSwatch.closest('.productCard');
      if (card) {
        card.querySelectorAll('[data-color-swatch]').forEach(s => s.removeAttribute('data-selected'));
        colorSwatch.setAttribute('data-selected', 'true');
        this._syncColorSelection(card, colorSwatch);
      }
      return;
    }

    const sizeOption = e.target.closest('[data-size-option]');
    if (sizeOption) {
      const variantId = this._resolveColorVariant(sizeOption) || sizeOption.dataset.variantId;
      if (variantId) this._addToCart(sizeOption, variantId);
      return;
    }

    this._closeSkuModalsOutside(e.target);
    this._closeCustomSelectsOutside(e.target);

    // Fechar ao clicar fora
    if (!e.target.closest('[data-size-popup]') && !e.target.closest('[data-product-add-toggle]')) {
      this._closeAllPopups();
    }
  }

  // ─── Popup ────────────────────────────────────────────────────────────────

  _openPopup(card) {
    const popup     = card.querySelector('[data-size-popup]');
    const toggleBtn = card.querySelector('[data-product-add-toggle]');
    if (!popup) return;
    popup.classList.add('is-open');
    popup.removeAttribute('hidden');
    toggleBtn?.setAttribute('aria-expanded', 'true');
  }

  _closePopup(card) {
    const popup     = card.querySelector('[data-size-popup]');
    const toggleBtn = card.querySelector('[data-product-add-toggle]');
    if (!popup) return;
    popup.classList.remove('is-open');
    popup.setAttribute('hidden', '');
    toggleBtn?.setAttribute('aria-expanded', 'false');
  }

  _closeAllPopups() {
    document.querySelectorAll('.productCard [data-size-popup].is-open').forEach(popup => {
      const card = popup.closest('.productCard');
      if (card) this._closePopup(card);
    });
  }

  _getSkuState(card) {
    let state = this._skuStates.get(card);
    if (state) return state;

    state = {
      card,
      modal: card.querySelector('[data-product-options]'),
      button: card.querySelector('[data-open-sku-modal]'),
      placeholder: null,
      restoreTimer: null,
    };
    this._skuStates.set(card, state);
    return state;
  }

  _toggleSkuModal(card) {
    const state = this._getSkuState(card);
    if (!state.modal) return;

    if (state.modal.classList.contains('is-open')) {
      this._closeSkuModal(state);
      return;
    }

    this._openSkuModal(state);
  }

  _openSkuModal(state) {
    clearTimeout(state.restoreTimer);

    if (!window.matchMedia('(min-width: 768px)').matches && state.modal.parentElement !== document.body) {
      state.placeholder = document.createComment('product-options-placeholder');
      state.modal.parentNode.insertBefore(state.placeholder, state.modal);
      document.body.appendChild(state.modal);
    }

    this._openSkuCards.add(state.card);
    const selectedSwatch = state.card.querySelector('[data-color-swatch][data-selected="true"]');
    if (selectedSwatch) this._syncColorSelection(state.card, selectedSwatch);
    else this._updateVariantAvailability(state.card, state.modal);
    window.requestAnimationFrame(() => state.modal.classList.add('is-open'));
  }

  _closeSkuModal(state, restoreImmediately = false) {
    if (!state.modal) return;

    state.modal.classList.remove('is-open');
    this._openSkuCards.delete(state.card);
    clearTimeout(state.restoreTimer);

    const restore = () => {
      if (!state.placeholder?.parentNode || state.modal.parentElement !== document.body) return;
      state.placeholder.parentNode.insertBefore(state.modal, state.placeholder);
      state.placeholder.remove();
      state.placeholder = null;
    };

    if (restoreImmediately) {
      restore();
      return;
    }

    state.restoreTimer = window.setTimeout(restore, 480);
  }

  _closeSkuModalsOutside(target) {
    this._openSkuCards.forEach(card => {
      const state = this._skuStates.get(card);
      if (!state || state.modal?.contains(target) || state.button?.contains(target)) return;
      this._closeSkuModal(state);
    });
  }

  _toggleCustomSelect(customSelect) {
    const options = customSelect?.querySelector('[data-custom-select-options]');
    const arrow = customSelect?.querySelector('[data-custom-select-arrow]');
    if (!options) return;

    const isHidden = options.classList.toggle('hidden');
    arrow?.classList.toggle('rotate-180', !isHidden);
  }

  _selectCustomOption(customSelect, option) {
    const select = customSelect?.querySelector('select');
    const valueLabel = customSelect?.querySelector('[data-custom-select-value]');
    const options = customSelect?.querySelector('[data-custom-select-options]');
    const arrow = customSelect?.querySelector('[data-custom-select-arrow]');
    if (!select || !valueLabel || !options) return;

    select.value = option.dataset.value || '';
    valueLabel.textContent = option.textContent.trim();
    options.classList.add('hidden');
    arrow?.classList.remove('rotate-180');
    select.dispatchEvent(new Event('change', { bubbles: true }));
    const optionsScope = customSelect.closest('[data-product-options]');
    this._updateVariantAvailability(this._findCardForOptions(optionsScope), optionsScope);
  }

  _closeCustomSelectsOutside(target) {
    document.querySelectorAll('[data-custom-select-options]:not(.hidden)').forEach(options => {
      const customSelect = options.closest('[data-custom-select]');
      if (customSelect?.contains(target)) return;
      options.classList.add('hidden');
      customSelect?.querySelector('[data-custom-select-arrow]')?.classList.remove('rotate-180');
    });
  }

  _syncColorSelection(card, swatch) {
    if (!card || !swatch) return;

    const position = Number(swatch.dataset.colorPosition);
    const colorValue = swatch.dataset.colorValue;
    if (!Number.isInteger(position) || !colorValue) return;

    const state = this._skuStates.get(card);
    const scopes = new Set([
      card.querySelector('[data-product-options]'),
      state?.modal,
    ]);

    scopes.forEach(scope => {
      if (!scope) return;

      const select = scope.querySelector(
        `[data-product-option-select][data-option-position="${position}"]`
      );
      if (!select) return;

      const option = [...select.options].find(candidate => candidate.value === colorValue);
      if (!option) return;

      select.value = colorValue;
      const valueLabel = select.closest('[data-custom-select]')?.querySelector('[data-custom-select-value]');
      if (valueLabel) valueLabel.textContent = option.textContent.trim();

      this._updateVariantAvailability(card, scope);
    });
  }

  _onKeydown(e) {
    if (e.key !== 'Escape') return;
    this._openSkuCards.forEach(card => {
      const state = this._skuStates.get(card);
      if (state) this._closeSkuModal(state);
    });
    this._closeCustomSelectsOutside(null);
  }

  _onResize() {
    if (!window.matchMedia('(min-width: 768px)').matches) return;
    this._openSkuCards.forEach(card => {
      const state = this._skuStates.get(card);
      if (state) this._closeSkuModal(state, true);
    });
  }

  // ─── Add to cart ──────────────────────────────────────────────────────────

  _findCardForOptions(optionsScope) {
    if (!optionsScope) return null;

    const directCard = optionsScope.closest('.productCard');
    if (directCard) return directCard;

    for (const card of this._openSkuCards) {
      const state = this._skuStates.get(card);
      if (state?.modal === optionsScope) return card;
    }

    return null;
  }

  _getCardVariants(card) {
    const variantsJson = card?.querySelector('[data-product-variants-json]');
    if (!variantsJson) return [];

    try {
      const variants = JSON.parse(variantsJson.textContent);
      return Array.isArray(variants) ? variants : [];
    } catch (error) {
      console.error('[ProductManager] Invalid variants JSON:', error);
      return [];
    }
  }

  _getSelectedVariant(card, optionsScope = null) {
    const scope = optionsScope || card?.querySelector('[data-product-options]') || card;
    const optionSelects = scope ? [...scope.querySelectorAll('[data-product-option-select]')] : [];
    const selectedOptions = optionSelects.map(select => select.value);

    if (!optionSelects.length) {
      return {
        variant: null,
        complete: true,
        optionSelects,
        selectedOptions,
      };
    }

    if (selectedOptions.some(value => !value)) {
      return {
        variant: null,
        complete: false,
        optionSelects,
        selectedOptions,
      };
    }

    const variant = this._getCardVariants(card).find(candidate => {
      return candidate.options?.length === selectedOptions.length
        && selectedOptions.every((value, index) => candidate.options[index] === value);
    });

    return {
      variant: variant || null,
      complete: true,
      optionSelects,
      selectedOptions,
    };
  }

  _updateVariantAvailability(card, optionsScope = null) {
    if (!card) return;

    const variants = this._getCardVariants(card);
    const scope = optionsScope || card.querySelector('[data-product-options]') || card;
    const optionSelects = [...scope.querySelectorAll('[data-product-option-select]')];
    if (!variants.length || !optionSelects.length) return;

    const selectedOptions = optionSelects.map(select => select.value);

    scope.querySelectorAll('[data-custom-select]').forEach(customSelect => {
      const select = customSelect.querySelector('[data-product-option-select]');
      const position = Number(select?.dataset.optionPosition) - 1;
      if (!select || !Number.isInteger(position) || position < 0) return;

      const availableValues = new Set(
        variants
          .filter(variant => variant.available && variant.options?.length === optionSelects.length)
          .filter(variant => {
            return selectedOptions.every((value, index) => {
              return index === position || !value || variant.options[index] === value;
            });
          })
          .map(variant => variant.options[position])
      );

      customSelect.querySelectorAll('[data-custom-select-option]').forEach(option => {
        const value = option.dataset.value;
        if (!value) return;

        const available = availableValues.has(value);
        option.disabled = !available;
        option.setAttribute('aria-disabled', String(!available));
      });

      [...select.options].forEach(option => {
        if (!option.value) return;

        const available = availableValues.has(option.value);
        option.disabled = !available;
      });
    });

    const addButton = scope.querySelector('[data-product-add-selected]');
    if (!addButton) return;

    const selection = this._getSelectedVariant(card, scope);
    const selectedVariant = selection.variant;
    const isIncomplete = selection.optionSelects.length > 0 && !selection.complete;
    const isAvailable = isIncomplete || Boolean(selectedVariant?.available);
    addButton.disabled = !isAvailable;
    addButton.dataset.variantId = selectedVariant?.available ? String(selectedVariant.id) : '';
    if (card.dataset.productUrl) addButton.dataset.productUrl = card.dataset.productUrl;
    const addLabel = addButton.querySelector('[data-product-add-label]');
    if (addLabel) addLabel.textContent = selectedVariant && !isIncomplete && !isAvailable ? 'Esgotado' : 'Comprar';
  }

  _resolveSelectedVariant(btn) {
    const optionsScope = btn.closest('[data-product-options]');
    const card = btn.closest('.productCard') || this._findCardForOptions(optionsScope);
    if (!card) return btn.dataset.variantId;

    const selection = this._getSelectedVariant(card, optionsScope);
    if (selection.optionSelects.length > 0) {
      if (selection.complete && selection.variant?.available) return selection.variant.id;
      return null;
    }

    const scope = optionsScope || card.querySelector('[data-product-options]') || card;
    const variantSelect = scope.querySelector('[data-product-variant-select]');
    return variantSelect?.value || btn.dataset.variantId;
  }

  // Resolve a variante a partir da cor (swatch) selecionada no card.
  _resolveColorVariant(btn) {
    const card = btn.closest('.productCard');
    if (!card) return null;

    const swatch = card.querySelector('[data-color-swatch][data-selected="true"]')
      || card.querySelector('[data-color-swatch]');
    if (!swatch) return null;

    const colorValue = swatch.dataset.colorValue;
    const position = parseInt(swatch.dataset.colorPosition, 10) - 1;
    const variantsJson = card.querySelector('[data-product-variants-json]');
    if (!colorValue || Number.isNaN(position) || !variantsJson) return null;

    try {
      const variants = JSON.parse(variantsJson.textContent);
      const match = variants.find(variant => {
        if (!variant.available || !variant.options) return false;
        return variant.options[position] === colorValue;
      });
      if (match?.id) return match.id;
    } catch (error) {
      console.error('[ProductManager] Invalid variants JSON:', error);
    }
    return null;
  }

  async _addToCart(btn, variantId) {
    const optionsScope = btn.closest('[data-product-options]');
    const card = btn.closest('.productCard') || this._findCardForOptions(optionsScope);
    const variant = this._getCardVariants(card).find(candidate => String(candidate.id) === String(variantId));
    if (variant && !variant.available) return;

    // Congela as dimensões do botão para evitar layout shift ao injetar SVG
    btn.style.width  = `${btn.offsetWidth}px`;
    btn.style.height = `${btn.offsetHeight}px`;

    const result = await window.CartManager?.add(variantId, { button: btn });

    btn.style.width  = '';
    btn.style.height = '';

    if (result?.success) {
      const card = btn.closest('.productCard');
      if (card) this._closePopup(card);
    }
  }

  destroy() {
    document.removeEventListener('click', this._onClick);
    document.removeEventListener('keydown', this._onKeydown);
    window.removeEventListener('resize', this._onResize);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.ProductManager = new ProductManager();
});
